<template>
  <div class="marquee" role="group" tabindex="0" :aria-label="label">
    <div class="marquee-track">
      <div class="marquee-copy">
        <slot />
      </div>
      <div class="marquee-copy" aria-hidden="true">
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The one marquee loop on the site.

   There were going to be two of these: the capability ticker on the home page
   and the model band on the AI page. They are the same motion, so they are the
   same component. Everything the loop is made of lives here: the track, the
   seam-free cycle, the edge mask, the pause and its focus ring, and the
   duration. A page supplies only the strip's own chrome, which is its rules,
   its padding and how its items look, because that is page furniture rather
   than part of the loop.

   The cycle is two identical copies of the slot on one track, and the track
   slides left by exactly half its own width, so the second copy lands where
   the first began and there is no visible seam. Each copy is wider than the
   viewport at every breakpoint, so the visible window is always covered.

   The strip is focusable and it pauses on hover and on focus-within, which is
   the WCAG 2.2.2 mechanism for auto-starting movement: a reader who cannot
   follow a moving row can stop it with the keyboard or the pointer. */

/* One speed for every marquee on the site. A per-page duration would let two
   strips of the same kind run at different rates for no reason a reader could
   name, which is the drift the shared component exists to prevent. */
$marquee-duration: 32s;

.marquee {
  mask-image: linear-gradient(90deg, transparent, #000 7%, #000 93%, transparent);
  overflow: hidden;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 7%, #000 93%, transparent);

  /* Inset, so the ring is not clipped away by the mask above. */
  &:focus-visible {
    box-shadow: inset 0 0 0 3px $ring-focus;
    outline: none;
  }

  &:hover .marquee-track,
  &:focus-within .marquee-track {
    animation-play-state: paused;
  }
}

.marquee-track {
  animation: marquee-slide $marquee-duration linear infinite;
  backface-visibility: hidden;
  display: flex;
  width: max-content;
  will-change: transform;
}

/* Never let a copy shrink, or the two halves stop matching and the loop seam
   becomes visible. */
.marquee-copy {
  display: flex;
  flex: 0 0 auto;
}

@keyframes marquee-slide {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-50%);
  }
}
</style>

<script>
export default {
  props: {
    /* The accessible name for the strip, e.g. "Capabilities we cover". */
    label: {
      type: String,
      default: ''
    }
  }
};
</script>
